import { fetchRequest } from "@common/helpers/util/requestMiddleware";
import { bisApuestatotalAPIPath, bisURL, fallbackBisURL } from "../constants";
import { sleep } from "@common/helpers";
import { writeTerminalLog } from "@common/helpers/log";
import Config from "@skin_config";

const checkTimeout = (asyncCb, actionName, getBaseURL) => (
  (...args) => (
    new Promise(async (resolve, reject) => {
      const timeout = setTimeout(() => {
        writeTerminalLog('warn', `BIS Apuestatotal API - ${getBaseURL()} ${actionName} Failed`, 'Request timeout');
        resolve({
          success: false,
          errors: [{ message: 'Request timeout, Please retry!' }],
        })
      }, 80 * 1000);

      try {
        const result = await asyncCb(...args);
        clearTimeout(timeout);
        resolve(result);
      } catch (e) {
        writeTerminalLog('warn', `BIS Apuestatotal API - ${getBaseURL()} ${actionName} Failed`, e.message);
        clearTimeout(timeout);
        reject(e);
      }
    })
  )
);

const EXTERNAL_VALIDATION_ERROR_CODE = 996;

class BISApuestatotalService {
  static baseUrl = bisURL;

  static makeRequest = async (actionName, token, body, { retryable } = {}) => {
    const headers = { 'x-api-token': token };

    const retryOnce = () => {
      writeTerminalLog('info', `BIS Apuestatotal API - ${this.baseUrl} ${actionName}`, 'Retrying 1');
      return this.makeRequest(actionName, token, body);
    };

    const fetchFailedResponse = {
      success: false,
      errors: [{ message: 'Something went wrong with validation' }]
    };

    try {
      const res = await fetchRequest(
        this.baseUrl,
        `${bisApuestatotalAPIPath}/${actionName}`,
        'POST',
        { headers, body },
        this.baseUrl === fallbackBisURL ? Config.verification.apiFallbackTimeout : Config.verification.apiTimeout
      );

      if (res?.success) {
        return res;
      }

      writeTerminalLog('warn', `BIS Apuestatotal API - ${this.baseUrl} ${actionName} Failed`, res?.errors.map((item) => item.message).join(', ') || '');

      if (res?.errors.some((item) => item.message.startsWith('Error al decodificar')) && this.baseUrl !== fallbackBisURL) {
        this.baseUrl = fallbackBisURL;
        return this.makeRequest(actionName, token, body);
      }

      // If we are on primary URL and got a non-external-validation error, try fallback once
      // const isExternalValidationError = res.errors?.some(item => item.errorCode === EXTERNAL_VALIDATION_ERROR_CODE);
      // if (!isExternalValidationError && this.baseUrl !== fallbackBisURL) {
      //   this.baseUrl = fallbackBisURL;
      //   return this.makeRequest(actionName, token, body, { retryable });
      // }

      if (retryable) {
        await sleep(5);
        return retryOnce();
      }

      return res.errors?.some(item => item.errorCode === EXTERNAL_VALIDATION_ERROR_CODE)
        ? res
        : fetchFailedResponse;

    } catch (e) {
      writeTerminalLog('warn', `BIS Apuestatotal API - ${this.baseUrl} ${actionName} Failed`, e.message);

      if (e && (e.code === 'ETIMEDOUT' || !e.response || e.message.startsWith('Error al decodificar')) && this.baseUrl !== fallbackBisURL) {
        this.baseUrl = fallbackBisURL;
        return this.makeRequest(actionName, token, body);
      }

      if (retryable) {
        await sleep(5);
        return retryOnce();
      }

      return fetchFailedResponse;
    }
  };

  static getBaseURL = () => BISApuestatotalService.baseUrl;

  static validate = checkTimeout((token, body) => (
    this.makeRequest('validate', token, body)
  ), 'validate', BISApuestatotalService.getBaseURL);

  static createBet = checkTimeout((token, body) => (
    this.makeRequest('createbet', token, body, { retryable: true })
  ), 'createbet', BISApuestatotalService.getBaseURL);

  static payBet = checkTimeout((token, body) => (
    this.makeRequest('paybet', token, body, { retryable: true })
  ), 'paybet', BISApuestatotalService.getBaseURL);
}

export default BISApuestatotalService;
